import { useState, useEffect, useRef, useCallback } from "react";
import { ChipNavButton } from "../../atoms/ChipNavigation";
import { GlobeSmIcon, ChevronDown } from "../../atoms/Icons";
import MenuSecondaryNav from "./MenuSecondaryNav";
import { useRouter } from "next/router";

export default function TopBar({
  languages = {},
  menuItemsSecondary,
  menuInfo,
  selectedLanguage,
  websiteTitle,
  translations,
  setOpenModal,
  setOpenModalSmall,
  other_languages,
  websiteType,
}) {
  const router = useRouter();
  const [openLangPopover, setOpenLangPopover] = useState(false);
  const popoverRef = useRef();
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showTopBar, setShowTopBar] = useState(true);
  const scrollThreshold = 50;

  // Handle scroll mechanism
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const scrollDifference = Math.abs(currentScrollY - lastScrollY);

    if (scrollDifference >= scrollThreshold) {
      const htmlElement = document.documentElement;
      if (currentScrollY > lastScrollY) {
        setShowTopBar(false);
        htmlElement.classList.remove("show-topbar");
      } else {
        setShowTopBar(true);
        htmlElement.classList.add("show-topbar");
      }
      setLastScrollY(currentScrollY);
    }
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.classList.add("show-topbar");
  }, []);

  const toggleLangPopover = () => {
    setOpenLangPopover(!openLangPopover);
  };

  const handleLanguageSelect = (language_code) => {
    if (other_languages && other_languages[language_code]) {
      router.push(
        { pathname: other_languages[language_code] },
        other_languages[language_code],
        { locale: language_code },
      );
    } else {
      router.push({ pathname: "/" }, "/", { locale: language_code });
    }

    setOpenLangPopover(false);
    setOpenModal(null);
  };

  // Open and close Language popover
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setOpenLangPopover(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleRegionSelection = () => {
    const event = new CustomEvent("openRegionSelection");
    window.dispatchEvent(event);
  };

  return (
    <div
      className={`container-custom flex relative z-40 bg-gray-350 duration-300 ${
        showTopBar ? "h-40" : "h-0"
      }`}
    >
      <nav
        className={`flex justify-between w-full items-center duration-300 ${
          showTopBar ? "translate-y-0" : "-translate-y-20"
        }`}
      >
        <ChipNavButton
          text={`${
            translations?.select_region ? translations?.select_region : ""
          }`}
          onClick={handleRegionSelection}
          icon={<GlobeSmIcon />}
          iconLeft={true}
        />
        <ul className="flex gap-x-12">
          <MenuSecondaryNav
            menuItemsSecondary={menuItemsSecondary}
            isInTopBar={true}
            setOpenModal={setOpenModal}
            setOpenModalSmall={setOpenModalSmall}
          />
          {Object.keys(languages || {}).length > 1 &&
          (websiteType == "petrotec-group" ||
            (websiteType == "petrotec" &&
              !process.env.NEXT_PUBLIC_ISO_COUNTRY_CODE)) ? (
            <li className="relative" ref={popoverRef}>
              <ChipNavButton
                text={languages[selectedLanguage] || selectedLanguage} // Fallback to code if name not found
                onClick={toggleLangPopover}
                icon={<ChevronDown />}
                iconRight={true}
              />
              <div
                className={`absolute z-20 right-0 mt-12 min-w-200 duration-300 ${
                  openLangPopover
                    ? "opacity-100 visible"
                    : "opacity-0 invisible"
                }`}
              >
                <ul className="flex flex-col gap-y-5 bg-gray-200 border border-gray-350 p-8 rounded-xxxs">
                  {Object.entries(languages ? languages : {}).map(
                    ([key, language]) => (
                      <li key={key}>
                        <button
                          className={`w-full text-left p-8 rounded-xxxs media-hover:hover:bg-gray-100 duration-300 ${
                            selectedLanguage === key
                              ? "bg-gray-100"
                              : "bg-gray-200"
                          }`}
                          onClick={() => handleLanguageSelect(key)}
                          aria-label={language || ""}
                        >
                          <span className="text-xxs font-medium leading-115 tracking-05">
                            {language}
                          </span>
                        </button>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </li>
          ) : (
            <></>
          )}
        </ul>
      </nav>
    </div>
  );
}
